import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import axios from 'axios'
import { AlertCircle, CheckCircle2, Eye, EyeOff, Lock } from 'lucide-react'
import { clearHeldInviteToken, heldInviteToken, passwordFeedback } from './inviteLink'

/**
 * Where an invite email lands: choose a password, once.
 *
 * Public by necessity — the person following the link has no session — so it
 * lives outside ProtectedRoute (see PublicOrProtected in App.tsx) and talks to
 * the API with bare axios rather than the shared instance, whose 401 handler
 * would clear a stored session and reload the page.
 *
 * The token arrives in the URL *fragment* (`#token=…`), which browsers never
 * send to a server, so it stays out of access logs and Referer headers. main.tsx
 * takes it off the address bar before Sentry initialises (Session Replay would
 * otherwise record it), and it lives here in memory only — see inviteLink.ts. A
 * reload therefore finds no token and asks for the emailed link again, which
 * still works: opening a link never spends it.
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

type LinkState =
  | { kind: 'checking' }
  | { kind: 'missing' }
  | { kind: 'invalid' }
  | { kind: 'disabled' }
  | { kind: 'error' }
  // The accept request got no answer (or a server fault) and the link no longer
  // validates, so the password MAY have been set. Never reported as a rejection.
  | { kind: 'unconfirmed' }
  | { kind: 'ready'; email: string; name: string }
  | { kind: 'done'; email: string }

function detailOf(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-[100dvh] w-screen overflow-x-hidden bg-[#0a0a0a] text-white">
      <div className="relative z-10 flex min-h-[100dvh] w-full items-center justify-center px-4 py-6">
        <div className="login-dialog w-full max-w-[370px]">
          <div className="px-6 pt-5">
            <div className="flex justify-center">
              <div className="h-[45px] w-[145px] overflow-hidden">
                <img
                  alt="Helios FinTech"
                  className="h-full w-full scale-[1.65] object-contain"
                  src="/branding/helios-login-logo.svg"
                />
              </div>
            </div>
            <div className="mt-2 text-center text-[8px] font-medium uppercase tracking-[0.32em] text-[#d9b768]">
              Capital Markets OS
            </div>
          </div>
          <div className="px-6 pb-6 pt-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

function Notice({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="login-panel text-center" role="status">
      <AlertCircle size={20} className="mx-auto mb-2 text-amber-300" />
      <p className="text-[14px] font-medium text-stone-100">{title}</p>
      <p className="mt-2 text-[12px] leading-relaxed text-stone-400">{body}</p>
      {action ? (
        <button type="button" className="login-action-gold mt-4" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

export default function AcceptInvitePage() {
  const [token] = useState<string | null>(() => heldInviteToken())
  const [link, setLink] = useState<LinkState>(token ? { kind: 'checking' } : { kind: 'missing' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [reveal, setReveal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Bumped by "Try again": re-runs the link check with the token held in memory.
  // Reloading the page cannot do that - the token is off the URL by design.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    axios
      .post(`${API_BASE}/auth/invite/validate`, { token })
      .then((res) => {
        if (cancelled) return
        const data = res.data as { valid: boolean; email?: string; name?: string }
        setLink(data.valid ? { kind: 'ready', email: data.email ?? '', name: data.name ?? '' } : { kind: 'invalid' })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setLink(statusOf(e) === 409 ? { kind: 'disabled' } : { kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [token, attempt])

  const feedback = password ? passwordFeedback(password) : null
  const mismatch = confirm.length > 0 && confirm !== password
  // Only length (a certain refusal) blocks the button. The character-class hint
  // is advisory: the server's policy is Unicode-aware and its answer is shown.
  const canSubmit = !busy && !!password && !feedback?.blocking && password === confirm

  const submit = async () => {
    if (!token || !canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const res = await axios.post(`${API_BASE}/auth/invite/accept`, { token, password })
      // The link is spent; do not keep it in memory either.
      clearHeldInviteToken()
      setPassword('')
      setConfirm('')
      setLink({ kind: 'done', email: (res.data as { email?: string }).email ?? '' })
    } catch (e) {
      const status = statusOf(e)
      if (status === 409) {
        setLink({ kind: 'disabled' })
      } else if (status === undefined || status >= 500) {
        // No answer, or a server fault: the password may have been committed
        // and only the reply lost. If the link still validates the request never
        // landed and nothing changed; if it does not, it was probably spent - so
        // say that, rather than calling the link dead and sending the person to
        // an administrator for a resend that is refused once a link is used.
        try {
          const res = await axios.post(`${API_BASE}/auth/invite/validate`, { token })
          if ((res.data as { valid: boolean }).valid) {
            setError('That did not go through, and nothing was changed. Try again.')
          } else {
            setLink({ kind: 'unconfirmed' })
          }
        } catch {
          setLink({ kind: 'unconfirmed' })
        }
      } else {
        setError(detailOf(e, 'Could not set your password. Try again.'))
        // A 400 is either "choose a stronger password" (link still good) or
        // "this link is dead". Ask which, rather than guess from the wording.
        axios
          .post(`${API_BASE}/auth/invite/validate`, { token })
          .then((res) => {
            if (!(res.data as { valid: boolean }).valid) setLink({ kind: 'invalid' })
          })
          .catch(() => undefined)
      }
    } finally {
      setBusy(false)
    }
  }

  if (link.kind === 'checking') {
    return (
      <Shell>
        <div className="flex justify-center py-8" role="status" aria-label="Checking your invite link">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/25 border-t-white" />
        </div>
      </Shell>
    )
  }

  if (link.kind === 'missing') {
    return (
      <Shell>
        <Notice
          title="Open the link from your invitation"
          body="This page needs the secure link from your invitation email. Open that email and use the button in it."
        />
      </Shell>
    )
  }

  if (link.kind === 'invalid') {
    return (
      <Shell>
        <Notice
          title="This invite link can’t be used"
          body="It has expired, has already been used, or was replaced by a newer invitation. Ask your administrator to send a new one."
        />
      </Shell>
    )
  }

  if (link.kind === 'disabled') {
    return (
      <Shell>
        <Notice
          title="Invitations are not available right now"
          body="Setting a password from an invitation is switched off. Contact your administrator."
        />
      </Shell>
    )
  }

  if (link.kind === 'error') {
    return (
      <Shell>
        <Notice
          title="We couldn’t check your link"
          body="The service did not respond. Check your connection, then try again."
          action={{
            label: 'Try again',
            onClick: () => {
              setLink({ kind: 'checking' })
              setAttempt((n) => n + 1)
            },
          }}
        />
      </Shell>
    )
  }

  if (link.kind === 'unconfirmed') {
    return (
      <Shell>
        <Notice
          title="We couldn’t confirm your password was set"
          body="Your connection dropped before we heard back, so it may have worked. Try signing in with the password you just chose. If that doesn’t work, ask your administrator to reset your password."
          action={{ label: 'Try signing in', onClick: () => window.location.assign('/') }}
        />
      </Shell>
    )
  }

  if (link.kind === 'done') {
    return (
      <Shell>
        <div className="login-panel text-center" role="status">
          <CheckCircle2 size={22} className="mx-auto mb-2 text-emerald-400" />
          <p className="text-[14px] font-medium text-stone-100">Your password is set</p>
          <p className="mt-2 text-[12px] leading-relaxed text-stone-400">
            Sign in as <span className="text-stone-200">{link.email}</span> with the password you just chose.
          </p>
          <button
            type="button"
            className="login-action-gold mt-4"
            onClick={() => window.location.assign('/')}
          >
            Continue to sign in
          </button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="login-panel">
        <p className="text-[14px] font-medium text-stone-100">Welcome{link.name ? `, ${link.name}` : ''}</p>
        <p className="mt-1 text-[12px] text-stone-400">
          Choose a password for <span className="text-stone-200">{link.email}</span>. This link works once.
        </p>

        <div className="mt-4 space-y-3.5">
          <div>
            <label
              htmlFor="invite-password"
              className="mb-2 block text-[11px] font-medium uppercase tracking-[0.28em] text-slate-400"
            >
              New password
            </label>
            <div className="relative">
              <Lock size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                id="invite-password"
                className="login-field pr-12"
                type={reveal ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void submit()}
              />
              <button
                type="button"
                aria-label={reveal ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-4 flex items-center rounded-full p-1 text-neutral-500 transition-colors hover:text-amber-200"
                onClick={() => setReveal((v) => !v)}
              >
                {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {feedback ? (
              <p className={`mt-1.5 text-[11px] ${feedback.blocking ? 'text-red-400' : 'text-amber-300'}`}>
                {feedback.message}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="invite-confirm"
              className="mb-2 block text-[11px] font-medium uppercase tracking-[0.28em] text-slate-400"
            >
              Confirm password
            </label>
            <div className="relative">
              <Lock size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                id="invite-confirm"
                className="login-field"
                type={reveal ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void submit()}
              />
            </div>
            {mismatch ? <p className="mt-1.5 text-[11px] text-red-400">The two passwords don’t match.</p> : null}
          </div>

          {error ? (
            <p className="text-[12px] text-red-400" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col items-center pt-1">
            <button type="button" className="login-action-gold" disabled={!canSubmit} onClick={() => void submit()}>
              {busy ? 'Setting password…' : 'Set password'}
            </button>
          </div>
        </div>
      </div>
    </Shell>
  )
}
